
import { createRoot } from 'react-dom/client';
import { StrictMode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App.tsx';
import './index.css';
import 'katex/dist/katex.min.css';
import { LANGUAGE_COOKIE_NAME, detectBrowserLanguage, saveUserLanguage } from './lib/i18n';
import { adminLog } from './lib/utils/admin-logger';
import { cleanupSeoPrerender } from './lib/seo-prerender-cleanup';

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Initialize language detection on app startup
const initLanguageDetection = () => {
  // Check if language preference already exists
  const savedLanguage = localStorage.getItem(LANGUAGE_COOKIE_NAME);
  if (!savedLanguage) {
    // If no language set, detect browser language and save it
    const detectedLanguage = detectBrowserLanguage();
    saveUserLanguage(detectedLanguage);
    adminLog(`Language detected and set to: ${detectedLanguage}`);
  }
};

// Initialize app-wide settings
initLanguageDetection();

// Create root and render the App with StrictMode and QueryClientProvider
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>
);

// Mark body as mounted so build-time prerendered SEO content (#seo-prerender)
// stays hidden once the SPA takes over. The element itself is already off-screen
// via inline CSS in the prerendered HTML; this class is a defence-in-depth signal.
if (typeof document !== 'undefined') {
  cleanupSeoPrerender(document);
}

